import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { EnumContainerLinguagem } from '../../../../../shared/enums';
import { ListaAcessoContainers } from './acesso-outros-containers/ListaAcessoContainers';
import { ListaAcessoComponentes } from './acesso-componentes/ListaAcessoComponentes';
import type { AcessoComponentes } from '../../../../../shared/services/api/c4-model/SistemasService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

import { TiposLinguagemService } from '../../../../../shared/services/api/c4-model/TiposLinguagemService';
import { ContainerService } from '../../../../../shared/services/api/c4-model/ContainerService';

type TipoLinguagem = {
  id: number;
  nome: string;
};

type TipoContainer = {
  id: number;
  nome: string;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (values: any) => void;
  initialValues?: {
    id?: string;
    tipo?: number;
    linguagem?: number;
    versao?: string;
    acessoContainers?: any[];
    acessoComponentes?: AcessoComponentes[];
  };
};

export const ModalContainers: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  initialValues,
}) => {
  const [tipo, setTipo] = useState<number | null>(null); // start empty for new
  const [linguagem, setLinguagem] = useState<number | null>(null);
  const [versao, setVersao] = useState('');
  const [acessoContainers, setAcessoContainers] = useState<any[]>([]);
  const [acessoComponentes, setAcessoComponentes] = useState<
    AcessoComponentes[]
  >([]);
  const [isValid, setIsValid] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [tiposLinguagem, setTiposLinguagem] = useState<TipoLinguagem[]>([]);
  const [tiposContainer, setTiposContainer] = useState<TipoContainer[]>([]);

  const getTipoLinguagemNome = (id: number): string => {
    const tipo = tiposLinguagem.find((t) => t.id === id);
    return tipo ? tipo.nome : `Linguagem ${id}`;
  };

  const getTipoContainerNome = (id: number): string => {
    const tipo = tiposContainer.find((t) => t.id === id);
    return tipo ? tipo.nome : `Tipo ${id}`;
  };

  useEffect(() => {
    // Carregar tipos de linguagem
    TiposLinguagemService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposLinguagem(data);
      }
    });

    // Carregar tipos de container
    ContainerService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposContainer(data);
      }
    });
  }, []);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        setTipo(initialValues.tipo ?? null);
        setLinguagem(initialValues.linguagem ?? null);
        setVersao(initialValues.versao || '');
        setAcessoContainers(initialValues.acessoContainers || []);
        setAcessoComponentes(initialValues.acessoComponentes || []);
      } else {
        setTipo(null);
        setLinguagem(null);
        setVersao('');
        setAcessoContainers([]);
        setAcessoComponentes([]);
      }
      setActiveTab(0); // Reset to first tab when modal opens
    }
  }, [open, initialValues]);

  useEffect(() => {
    const allFilled = Boolean(tipo !== null && linguagem !== null && versao.trim());
    setIsValid(allFilled);
  }, [tipo, linguagem, versao]);

  const handleSave = () => {
    const values: any = {
      tipo,
      linguagem,
      versao,
      acessoContainers,
      acessoComponentes,
    };
    if (initialValues && initialValues.id) values.id = initialValues.id;
    if (onSave) onSave(values);
    onClose();
  };

  const handleAcessoContainersChange = (newAcessos: any[]) => {
    setAcessoContainers(newAcessos);
  };

  const handleAcessoComponentesChange = (newAcessos: AcessoComponentes[]) => {
    setAcessoComponentes(newAcessos);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Container</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={4}>
          {/* Dados Gerais - Sempre visível */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Dados Gerais
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="tipo-label">Tipo</InputLabel>
                <Select
                  labelId="tipo-label"
                  id="tipo-select"
                  value={tipo ?? ''}
                  label="Tipo"
                  onChange={(e) =>
                    setTipo(e.target.value === '' ? null : Number(e.target.value))
                  }
                >
                  {tiposContainer.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel id="linguagem-label">Linguagem</InputLabel>
                <Select
                  labelId="linguagem-label"
                  id="linguagem-select"
                  value={linguagem ?? ''}
                  label="Linguagem"
                  onChange={(e) =>
                    setLinguagem(e.target.value === '' ? null : Number(e.target.value))
                  }
                >
                  {tiposLinguagem.map((tipoLing) => (
                    <MenuItem key={tipoLing.id} value={tipoLing.id}>
                      {tipoLing.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Versão"
                margin="normal"
                value={versao}
                onChange={(e) => setVersao(e.target.value)}
                placeholder="Ex: 1.0.0"
              />
            </Box>
          </Box>

          <Divider />

          {/* Tabs para Acessos */}
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="acessos tabs"
              >
                <Tab label="Acesso a Containers" />
                <Tab label="Acesso a Componentes" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <ListaAcessoContainers
                initialRows={
                  acessoContainers.length > 0
                    ? [
                      {
                        id: initialValues?.id || '',
                        sistemaId: '',
                        tipo: tipo ?? 1,
                        linguagem: linguagem ?? EnumContainerLinguagem.REACTJS,
                        versao: versao || '',
                        acessoContainers: acessoContainers,
                        acessoComponentes: [],
                      },
                    ]
                    : []
                }
                onAcessoContainersChange={handleAcessoContainersChange}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <ListaAcessoComponentes
                initialRows={acessoComponentes}
                onAcessoComponentesChange={handleAcessoComponentesChange}
              />
            </TabPanel>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          color="primary"
          disabled={!isValid}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
